import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import {
  Search, BarChart3, Megaphone, Code2, PenTool, ShoppingBag,
  ArrowRight, Star, TrendingUp, Users, Globe, Sparkles,
} from "lucide-react";
import teamImg from "@/assets/team.jpg";
import patternImg from "@/assets/pattern.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "BigBrandBooster — Global Digital Marketing Agency" },
      { name: "description", content: "Performance-driven digital marketing: SEO, PPC, social, content & web that drive measurable growth for ambitious brands." },
      { property: "og:title", content: "BigBrandBooster — Global Digital Marketing Agency" },
      { property: "og:description", content: "Performance-driven digital marketing for ambitious brands." },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: HomePage,
});

const services = [
  { icon: Search, title: "Search Engine Optimization", desc: "Technical SEO, content & authority programs that compound traffic and rankings." },
  { icon: Megaphone, title: "Paid Media & PPC", desc: "Google, Meta, LinkedIn & programmatic campaigns engineered for ROAS." },
  { icon: PenTool, title: "Content & Creative", desc: "Brand storytelling, video, design and copy built for performance." },
  { icon: BarChart3, title: "Analytics & Insights", desc: "Attribution, dashboards and CRO that turn data into decisions." },
  { icon: Code2, title: "Web & Product", desc: "High-performance websites, landing pages and conversion experiences." },
  { icon: ShoppingBag, title: "Ecommerce Growth", desc: "End-to-end marketplace, D2C and retention strategies that scale revenue." },
];

const stats = [
  { value: "12+", label: "Years of expertise" },
  { value: "350+", label: "Brands scaled" },
  { value: "4.9★", label: "Client satisfaction" },
  { value: "27", label: "Countries served" },
];

function HomePage() {
  return (
    <>
      <PageHero
        eyebrow="Digital Marketing Agency"
        title={<>Let's Start Your <span className="text-accent">Growth Journey</span> Together</>}
        description="We blend strategy, technology and creativity to engineer measurable outcomes for ambitious brands across the globe."
      >
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-background px-6 py-3.5 text-sm font-semibold text-ink shadow-soft hover:bg-background/95">
          Get a Free Audit <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-6 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10">
          Explore Services
        </Link>
      </PageHero>

      {/* Stats strip */}
      <section className="border-b border-border bg-secondary/40">
        <div className="container-page grid grid-cols-2 md:grid-cols-4 gap-8 py-12">
          {stats.map((s) => (
            <div key={s.label} className="text-center md:text-left">
              <div className="font-display text-4xl md:text-5xl font-bold text-gradient">{s.value}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="container-page py-24 grid lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <img src={teamImg} alt="BigBrandBooster team" loading="lazy" className="rounded-3xl shadow-elegant object-cover aspect-[4/3]" />
          <div className="absolute -bottom-8 -right-4 md:-right-8 max-w-xs rounded-2xl bg-card p-5 shadow-elegant border border-border">
            <div className="flex items-center gap-2 text-accent">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
            </div>
            <p className="mt-2 text-sm text-foreground/80">"They engineered 3.4x ROAS in 90 days — a real growth partner."</p>
            <p className="mt-2 text-xs font-semibold text-ink">— Aanya M., CMO at Lumen</p>
          </div>
        </div>
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">About BigBrandBooster</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-ink">A performance partner, not just an agency.</h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            We're a team of strategists, creatives and engineers obsessed with brand outcomes. From category leaders to challenger startups, we build digital programs that move metrics that matter.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              [TrendingUp, "Outcome obsessed"],
              [Users, "Senior-led teams"],
              [Globe, "Global footprint"],
              [Sparkles, "Creative engineering"],
            ].map(([Icon, label]) => {
              const I = Icon as typeof TrendingUp;
              return (
                <li key={label as string} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-gradient text-primary-foreground"><I className="h-4 w-4" /></span>
                  <span className="text-sm font-medium text-ink">{label as string}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Services */}
      <section className="bg-secondary/40 py-24">
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">What we do</span>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-ink">Full-funnel services, engineered to perform.</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all">
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="group relative rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:shadow-elegant">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-gradient text-primary-foreground shadow-soft">
                  <s.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition">
                  Learn more <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page py-24">
        <div className="relative overflow-hidden rounded-3xl">
          <img src={patternImg} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-hero-gradient opacity-90" />
          <div className="relative px-8 md:px-16 py-20 text-center text-primary-foreground">
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight">Ready to grow what matters?</h2>
            <p className="mt-5 max-w-2xl mx-auto text-lg text-primary-foreground/85">
              Tell us your goals — we'll build the plan. Get a complimentary growth audit within 48 hours.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-background px-7 py-3.5 text-sm font-semibold text-ink shadow-soft hover:bg-background/95">
              Start the conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
